/**
 * useLiveBridge — thin compositor
 *
 * Delegates to four focused hooks:
 *   useDraftState      — bilingual draft state + localStorage
 *   useRecognition     — speech recognition + unified restart timer (iOS fix)
 *   useViewerSession   — viewer auth, polling, session management
 *   useNetworkSync     — message push, preview debounce, translation fill
 *
 * The return shape is identical to the original monolithic hook —
 * no changes required in App.js.
 */
import { useDraftState } from "./useDraftState";
import { useRecognition } from "./useRecognition";
import { useViewerSession } from "./useViewerSession";
import { useNetworkSync } from "./useNetworkSync";

export function useLiveBridge({ page, t }) {
  // ── 1. Draft state ────────────────────────────────────────────────────────
  const draft = useDraftState();

  // ── 2. Recognition ────────────────────────────────────────────────────────
  const recognition = useRecognition({
    page,
    t,
    onRecognitionFinal: draft.onRecognitionFinal,
    onRecognitionInterim: draft.onRecognitionInterim,
    commitPendingRecognitionText: draft.commitPendingRecognitionText
  });

  // ── 3. Viewer session ─────────────────────────────────────────────────────
  const viewer = useViewerSession({ page, t });

  // ── 4. Network sync ───────────────────────────────────────────────────────
  const sync = useNetworkSync({
    page,
    t,
    draft,
    viewer,
    inputStatus: recognition.liveBridgeInputStatus
  });

  // ── Return (same shape as original) ──────────────────────────────────────
  return {
    // Draft state
    liveBridgeSender: draft.liveBridgeSender,
    liveBridgeDraft: draft.liveBridgeDraft,
    liveBridgeChineseDraft: draft.liveBridgeChineseDraft,
    liveBridgeLatestEnglish: draft.liveBridgeLatestEnglish,
    liveBridgeLatestChinese: draft.liveBridgeLatestChinese,
    liveBridgeMode: draft.liveBridgeMode,
    setLiveBridgeSender: draft.setLiveBridgeSender,
    setLiveBridgeDraft: draft.setLiveBridgeDraft,
    setLiveBridgeChineseDraft: draft.setLiveBridgeChineseDraft,
    setLiveBridgeMode: draft.setLiveBridgeMode,

    // Recognition
    liveBridgeSpeechSupported: recognition.liveBridgeSpeechSupported,
    liveBridgeRecognitionLanguage: recognition.liveBridgeRecognitionLanguage,
    liveBridgeSpeechHint: recognition.liveBridgeSpeechHint,
    liveBridgeInputStatus: recognition.liveBridgeInputStatus,
    liveBridgeRecordingLabel: recognition.liveBridgeRecordingLabel,
    liveBridgeListeningLabel: recognition.liveBridgeListeningLabel,
    handleLiveBridgeStartListening: recognition.handleLiveBridgeStartListening,
    handleLiveBridgeStopListening: recognition.handleLiveBridgeStopListening,

    // Viewer session
    liveBridgeViewerPasswordInput: viewer.liveBridgeViewerPasswordInput,
    liveBridgeViewerPassword: viewer.liveBridgeViewerPassword,
    liveBridgeMessages: viewer.liveBridgeMessages,
    liveBridgePreview: viewer.liveBridgePreview,
    liveBridgeViewerLoading: viewer.liveBridgeViewerLoading,
    liveBridgeViewerError: viewer.liveBridgeViewerError,
    liveBridgeConnectionStatus: viewer.liveBridgeConnectionStatus,
    liveBridgeConnectionLabel: viewer.liveBridgeConnectionLabel,
    liveBridgeUpdatedLabel: viewer.liveBridgeUpdatedLabel,
    liveBridgeViewerFeedRef: viewer.liveBridgeViewerFeedRef,
    setLiveBridgeViewerPasswordInput: viewer.setLiveBridgeViewerPasswordInput,
    handleLiveBridgeUnlock: viewer.handleLiveBridgeUnlock,
    handleLiveBridgeLock: viewer.handleLiveBridgeLock,

    // Network sync
    liveBridgeSending: sync.liveBridgeSending,
    liveBridgeTranslating: sync.liveBridgeTranslating,
    liveBridgeSendError: sync.liveBridgeSendError,
    liveBridgeSendSuccess: sync.liveBridgeSendSuccess,
    handleLiveBridgeSend: sync.handleLiveBridgeSend,
    handleLiveBridgeFillMissingSide: sync.handleLiveBridgeFillMissingSide,
    handleLiveBridgeSendTestMessage: sync.handleLiveBridgeSendTestMessage,
    handleLiveBridgeStartNewSession: sync.handleLiveBridgeStartNewSession,
    handleLiveBridgeClearDrafts: sync.handleLiveBridgeClearDrafts
  };
}

export default useLiveBridge;
